/**
 * Runtime plugin loading (design spec §9.10, spike S3): after boot the app asks `GET /api/plugins`
 * and `import()`s each enabled plugin's ESM bundle. Bundles import `react`, `react/jsx-runtime`,
 * `react-dom` and `@drang/sdk` as bare specifiers; the import map in index.html points those at
 * `/shims/*.js`, which re-export the host's own instances from `window.__drang`. One React, shared
 * registries, hooks work across the boundary.
 */
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as jsxRuntime from "react/jsx-runtime";
import * as sdk from "../sdk";

export interface PluginManifest {
  id: string;
  kind: string[];
  version: string;
  sdkVersion: string;
  web: string | null;
  enabled: boolean;
  builtin: boolean;
  config: Record<string, unknown>;
  configSchema: Record<string, unknown>;
}

declare global {
  interface Window {
    __drang: {
      React: typeof React;
      ReactDOM: typeof ReactDOM;
      jsxRuntime: typeof jsxRuntime;
      sdk: typeof sdk;
    };
    drang: {
      register(manifest: { id: string; setup?(config: Record<string, unknown>): void }): void;
      configs: Record<string, Record<string, unknown>>;
    };
  }
}

const loaded = new Map<string, "ok" | "failed">();

export function installPluginHost(): void {
  window.__drang = { React, ReactDOM, jsxRuntime, sdk };
  window.drang = {
    configs: {},
    register(manifest) {
      manifest.setup?.(window.drang.configs[manifest.id] ?? {});
      loaded.set(manifest.id, "ok");
    },
  };
}

export async function loadPlugins(): Promise<{ id: string; status: "ok" | "failed" }[]> {
  let list: PluginManifest[] = [];
  try {
    const res = await fetch("/api/plugins", { credentials: "same-origin" });
    if (res.ok) list = ((await res.json()) as { plugins: PluginManifest[] }).plugins;
  } catch {
    return [];
  }
  const out: { id: string; status: "ok" | "failed" }[] = [];
  for (const p of list) {
    if (!p.web || loaded.has(p.id)) continue;
    window.drang.configs[p.id] = p.config ?? {};
    try {
      await import(/* @vite-ignore */ p.web);
      if (!loaded.has(p.id)) loaded.set(p.id, "ok");
    } catch (e) {
      console.warn(`[drang] plugin ${p.id} did not load`, e);
      loaded.set(p.id, "failed");
    }
    out.push({ id: p.id, status: loaded.get(p.id)! });
  }
  document.documentElement.dataset.plugins = [...loaded.entries()].map(([id, s]) => `${id}:${s}`).join(",");
  return out;
}
